import Button from "components/form/Button";
import InputField from "components/form/InputField";
import MessageToast from "components/ui-component/MessageToast";
import { ExpiredAgreementIcon } from "icons/action/ExpiredAgreementIcon";
import InfoIcon from "icons/dashboard/InfoIcon";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import {
  paymentReceiptDownloadPdf,
  receiptIdGet,
} from "store/slices/paymentsSlice";
import {
  decode,
  downloadPdfFromApi,
  encode,
  validateEmail,
} from "utils/HelperFunctions";
import { useTestAttrsFactory } from "utils/testid";

type BankDetails = {
  email: string;
  bank: string;
  routing: string;
  account: string;
  swift: string;
  dueDate: string;
};
interface WirePaymentSuccessProps {
  bankDetails: BankDetails;
  isRetry?: boolean;
  sendEmail: any;
  orderId?: number | string;
  lead_id: number | string;
  className?: string;
  showSuccessToast?: boolean;
  isInvoice?: boolean;
  invoiceNumber?: string;
  invoiceSlug?: string;
}
const WirePaymentSuccess = ({
  bankDetails,
  isRetry = false,
  sendEmail,
  orderId,
  lead_id,
  className = "mt-[5%]",
  showSuccessToast,
  isInvoice = false,
  invoiceNumber,
  invoiceSlug,
}: WirePaymentSuccessProps) => {
  const { email, bank, routing, account, swift, dueDate } = bankDetails;
  const navigate = useNavigate();
  const userData = useUserData();
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = cluster_id
    ? decode(cluster_id || "", true)
    : decode(id || "").split("_")[1];

  const accountLeadData = useLeadData();
  const vanityUrl = usePortalVanityUrl();
  const dispatch = useAppDispatch();
  const [inputEmail, setInputEmail] = useState("");
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [receiptId, setReceiptId] = useState<any>(null);
  const [isRecieptDownloaded, setIsRecieptDownloaded] =
    useState<boolean>(false);
  const [msg, setMsg] = useState<any>({
    variant: "",
    content: null,
  });
  const testAttrs = useTestAttrsFactory();

  const handleGetReceiptID = async (params: any) => {
    try {
      const response: any = await dispatch(receiptIdGet(params)).unwrap();
      if (response?.body?.success) {
        setReceiptId(response.body.data?.id);
        return response.body.data?.id;
      }
      return params?.orders_id;
    } catch (error) {
      console.error("Failed to get receipt ID:", error);
    }
  };

  const handleDownload = async () => {
    setIsDownloadLoading(true);
    let receipt_id = receiptId;
    if (!receipt_id) {
      receipt_id = await handleGetReceiptID({
        orders_id: orderId,
      });
    }

    await onPaymentDownload(orderId, receipt_id);
  };

  const onPaymentDownload = async (id: any, receiptId?: any) => {
    setIsDownloadLoading(true);
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    try {
      let payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id || lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: clusterId || userData?.data?.accountData?.clusterid,
        isFromPortal: true,
        isWirePending: "Y",
      };
      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `Wire transfer request - ${receiptId || id}.pdf`,
        (loading) => setIsDownloadLoading(loading ? true : false)
      );

      if (showSuccessToast) {
        setIsRecieptDownloaded(true);
        setMsg({
          ...msg,
          variant: "success",
          content: "Receipt downloaded successfully",
        });
      }
    } catch (error: any) {
      console.log("payment download err: -->", error?.message);
    }
  };

  const onInvoiceDownload = async () => {
    setIsDownloadLoading(true);
    const decoded = decode(invoiceSlug || "");
    try {
      let payload = {
        invoice_slug: invoiceSlug,
        lead_id: lead_id,
        cluster_id: decoded[1],
        url: `${vanityUrl}invoice/${invoiceSlug}`,
      };

      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `Invoice - ${invoiceNumber || invoiceSlug}.pdf`,
        (loading) => setIsDownloadLoading(loading ? true : false)
      );
    } catch (error) {
      console.log("error ======", error);
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!validateEmail(inputEmail)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    setIsEmailLoading(true);
    try {
      let emails = [inputEmail];
      let value = {
        invoice_id: orderId,
        emails: emails,
        isRealTime: true,
        bank_name: bank,
        routing_number: routing,
        account_number: account,
        swift_code: swift,
        shouldSendAll: false,
      };
      await sendEmail(value);
      setInputEmail("");
      if (showSuccessToast) {
        setIsEmailSent(true);
        setMsg({
          ...msg,
          variant: "success",
          content: "Receipt sent successfully",
        });
      }
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleSendInvoiceEmail = async () => {
    if (!validateEmail(inputEmail)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    setIsEmailLoading(true);
    try {
      let value = {
        invoice_slug: invoiceSlug,
        email: inputEmail,
        bank_name: bank,
        routing_number: routing,
        account_number: account,
        swift_code: swift,
        shouldSendAll: false,
      };
      await sendEmail(value);
      if (showSuccessToast) {
        setIsEmailSent(true);
        setMsg({
          ...msg,
          variant: "success",
          content: "Receipt sent successfully",
        });
      }
      setInputEmail("");
    } catch (error) {
      console.log("error ======", error);
    } finally {
      setIsEmailLoading(false);
    }
  };

  return (
    <>
      <div
        className={`w-full h-full text-center max-w-[620px] lg:py-0 py-5 ${
          isRetry ? "lg:px-0 px-5" : "px-5"
        }`}
        {...testAttrs("wire-payment-pending", "Wire transfer payment pending")}
      >
        <div className="h-full flex flex-col justify-center items-center gap-10">
          <ExpiredAgreementIcon aria-hidden="true" />
          <div className="flex flex-col gap-4 justify-center items-center">
            <p className="lg:text-2xl text-xl font-medium text-txtPrimary">
              Wire transfer payment pending
            </p>
          </div>
          <div className="w-full flex flex-col gap-3 text-sm">
            <p className="w-full text-xs text-left text-txtParagraph font-bold text-capitalize">
              SEND THE PAYMENT TO:
            </p>
            <div className="w-full h-[1px] bg-strokePrimary" />
            <div className="w-full flex justify-between items-center text-sm">
              <p className="text-txtSecondary">Bank name</p>
              <p className="text-txtPrimary">{bank}</p>
            </div>
            <div className="w-full flex justify-between items-center text-sm">
              <p className="text-txtSecondary">Routing number</p>
              <p className="text-txtPrimary">{routing}</p>
            </div>
            <div className="w-full flex justify-between items-center text-sm">
              <p className="text-txtSecondary">Account number</p>
              <p className="text-txtPrimary">{account}</p>
            </div>
            <div className="w-full flex justify-between items-center text-sm">
              <p className="text-txtSecondary">SWIFT code</p>
              <p className="text-txtPrimary">{swift}</p>
            </div>
            <div className="w-full h-[1px] bg-strokePrimary" />
            <div className="w-full break-words text-[13px] text-txtParagraph flex gap-1 text-start">
              <div className="mt-1">
                <InfoIcon aria-hidden="true" />
              </div>
              Funds must be received by {dueDate}. Wire transfers can take up to
              two business days to process. Payments not received by the due
              date will automatically be marked as a failed payment.
            </div>
          </div>
          <div
            className={`flex flex-col gap-[30px] mx-3.5 px-2.5 items-center justify-center text-center ${className}`}
          >
            <div className="lg:text-base text-sm text-txtParagraph">
              A copy of the wire instruction will automatically be sent to{" "}
              <span className="font-bold">{email}.</span> To send a copy to an
              additional email, enter the address below.
            </div>
            <div className="w-full">
              {" "}
              <InputField
                type="email"
                name="email"
                label=""
                placeholder="Enter your email address"
                value={inputEmail}
                onChange={(e) => {
                  setInputEmail(e.target.value);
                  setIsErr(false);
                  setErrMsg(undefined);
                }}
                className="!py-3"
                error={errMsg}
                isErr={isErr}
                {...testAttrs(
                  "wire-payment-email",
                  "Additional email for wire transfer receipt"
                )}
              />
            </div>
            <div className="flex justify-center items-center gap-6 flex-col w-full">
              <div className="flex justify-between gap-5 w-full mt-2">
                <Button
                  text="Send Email"
                  className="w-full"
                  isLoading={isEmailLoading}
                  {...testAttrs(
                    "send-wire-payment-email",
                    "Send wire payment email"
                  )}
                  onClick={() => {
                    if (isInvoice) {
                      handleSendInvoiceEmail();
                    } else {
                      handleSendEmail();
                    }
                  }}
                  isSuccess={isEmailSent}
                  isDisabled={inputEmail.length === 0 && !isEmailSent}
                />
                <Button
                  text="Download"
                  className="w-full"
                  isLoading={isDownloadLoading}
                  isSuccess={isRecieptDownloaded}
                  {...testAttrs(
                    "download-wire-payment",
                    "Download wire payment"
                  )}
                  onClick={() => {
                    if (isInvoice) {
                      onInvoiceDownload();
                    } else {
                      handleDownload();
                    }
                  }}
                />
              </div>
              <p
                className=" lg:text-base text-sm text-btnPrimaryActive cursor-pointer hover:!text-btnPrimaryHover "
                role="button"
                tabIndex={0}
                {...testAttrs("back-to-dashboard", "Back to dashboard")}
                onClick={() => {
                  let clusterId = cluster_id
                    ? cluster_id
                    : userData?.data?.accountData?.clusterid
                      ? encode(
                          `${userData?.data?.accountData?.clusterid}`,
                          "ci_"
                        )
                      : encode(decode(id || "")?.split("_")[1], "ci_");

                  navigate(`/${clusterId}/dashboard`);
                }}
              >
                Back to dashboard
              </p>
            </div>
          </div>
        </div>
        <div className="relative text-left max-w-[560px] !bottom-0 z-[9999999]">
          <MessageToast
            variant={msg?.variant}
            content={msg?.content}
            isProgressBar
            width="max-w-[580px] w-full px-5"
            className=" !absolute !right-1/2 !translate-x-1/2"
            onCloseClick={() => {
              setMsg({
                ...msg,
                variant: "",
                content: null,
              });
              setIsEmailSent(false);
              setIsRecieptDownloaded(false);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default WirePaymentSuccess;
